import { useNavigate as useReactRouterNavigate } from 'react-router-dom';
import { buildRoute } from '../config/routes';

/**
 * Enhanced navigation hook that automatically handles app base path
 * 
 * Usage:
 * const navigate = useAppNavigation();
 * navigate('/settings') // Automatically becomes '/app/settings'
 * navigate('settings')  // Also becomes '/app/settings'
 * navigate.to.SETTINGS  // Uses pre-built route constants
 */
export const useAppNavigation = () => {
  const reactNavigate = useReactRouterNavigate();
  
  /**
   * Navigate to a path within the app
   * @param {string|number} path - Path to navigate to, or -1 for back
   * @param {object} options - Navigation options (state, replace, etc.)
   */
  const navigate = (path, options = {}) => {
    if (typeof path === 'number') {
      // Handle navigate(-1) for back navigation
      reactNavigate(path);
      return;
    }
    
    // Build the full route and navigate
    const fullPath = buildRoute(path);
    reactNavigate(fullPath, options);
  };
  
  // Add route constants for convenience
  navigate.to = {
    HOME: () => navigate(''),
    LOGIN: () => navigate('login'),
    FORGOT_PASSWORD: () => navigate('forgot-password'),
    RESET_PASSWORD: (token) => navigate(token ? `reset-password/${token}` : 'reset-password'),
    FIND: () => navigate('find'),
    ADD: () => navigate('add'),
    ADD_ITEM: (boxId) => navigate(boxId ? `add-item?boxId=${boxId}` : 'add-item'),
    CREATE_BOX: (options) => navigate('create-box', options),
    ALL_ITEMS: () => navigate('all-items'),
    ALL_BOXES: () => navigate('all-boxes'),
    OFFLINE_ITEMS: () => navigate('offline-items'),
    SETTINGS: () => navigate('settings'),
    SUBSCRIPTION: () => navigate('subscription'),
    PRO_FEATURES: () => navigate('pro-features'),
    COMING_SOON: () => navigate('coming-soon'),
    PROFILE: () => navigate('profile'),
    TAGS: () => navigate('tags'),
    LOCATIONS: () => navigate('locations'),
    IMAGE_TEST: () => navigate('image-test'),
    ASSIGN_QR: () => navigate('assign-qr'),
    TERMS_OF_SERVICE: () => navigate('terms'),
    PRIVACY_POLICY: () => navigate('privacy'),
    DATA_DELETION: () => navigate('data-deletion'),
    BOX_DETAIL: (boxId) => navigate(`box/${boxId}`),
    BOX_EDIT: (boxId) => navigate(`box/${boxId}/edit`),
    ITEM_DETAIL: (itemId) => navigate(`item/${itemId}`),
    ITEM_EDIT: (itemId) => navigate(`item/${itemId}`),
    LET_GO: () => navigate('let-go'),
    EXIT_BIN: () => navigate('exit-bin'),
  };
  
  // Add back navigation
  navigate.back = () => reactNavigate(-1);
  
  // Add replace navigation (for redirects without adding to history)
  navigate.replace = (path) => {
    const fullPath = buildRoute(path);
    reactNavigate(fullPath, { replace: true });
  };
  
  return navigate;
};

export default useAppNavigation;
